import { GridCellParams, GridColDef } from '@mui/x-data-grid';
import { Timestamp } from 'firebase/firestore';
import { formatToPriceInDollars, formatToShortString } from '../../../../utils/FormatValue';
import { SalesCommItem, GroupedSalesCommItem } from '../../../FutureOrder/SalesCommComponents/SalesCommUtils';
import { KeyboardArrowDown, KeyboardArrowRight } from '@mui/icons-material';
import { Material } from '../../../../sp-types/materialType';

export const getInventoryCols = (
  price: number,
  unit: string,
  getCurrencyInUSD: (_curr: string) => number,
  material?: { hasRatio?: boolean },
): GridColDef[] => [
  {
    field: 'poId',
    headerName: 'PO.',
    headerClassName: 'bg-gray-200  border',
    minWidth: 200,
    width: 200,
    renderCell: ({ row }: GridCellParams) => {
      return (
        <div className="pl-2" style={{ borderLeftColor: row.color, color: row.dontSum ? '#ef4444' : 'inherit' }}>
          {row.isPO ? `${row.poId} / ${row.poBatchId}` : 'Storage'}
        </div>
      );
    },
  },
  {
    field: 'supplier',
    headerName: 'Supplier',
    minWidth: 150,
    width: 150,
    renderCell: ({ row }: GridCellParams) => {
      return <div style={{ color: row.dontSum ? '#ef4444' : 'inherit' }}>{row.supplier || '-'}</div>;
    },
    type: 'string',
  },

  {
    field: 'quantity',
    headerName: 'Quantity',
    renderCell: ({ row }: GridCellParams) => {
      return (
        <div style={{ color: row.dontSum ? '#ef4444' : 'inherit' }}>
          {formatToShortString(row.quantity, 1)} {unit}
        </div>
      );
    },
    type: 'string',
  },
  {
    field: 'suppliedQty',
    headerName: 'Supplied',
    renderCell: ({ row }: GridCellParams) => (
      <div style={{ color: row.dontSum ? '#ef4444' : 'inherit' }}>
        {row.suppliedQty || 0} {unit}
      </div>
    ),
    type: 'string',
  },
  {
    field: 'remainingQty',
    headerName: 'Remaining',
    renderCell: ({ row: { quantity, suppliedQty, dontSum } }: GridCellParams) => (
      <div style={{ color: dontSum ? '#ef4444' : 'inherit' }}>
        {quantity - (suppliedQty || 0)} {unit}
      </div>
    ),
    type: 'string',
  },
  ...(material?.hasRatio
    ? ([
        {
          field: 'ratio',
          headerName: 'Ratio',
          renderCell: ({ row }: GridCellParams) => (
            <div style={{ color: row.dontSum ? '#ef4444' : 'inherit' }}>
              {row.ratio
                ? row.ratio.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                : ''}
            </div>
          ),
          type: 'string',
        },
      ] as GridColDef[])
    : []),
  {
    field: 'reportingTime',
    headerName: 'Due date',
    type: 'date',
    minWidth: 100,
    width: 100,
    valueGetter: (value: Timestamp) => value?.toDate(),
    renderCell: ({ row }: GridCellParams) => (
      <span style={{ color: row.dontSum ? '#ef4444' : 'inherit' }}>
        {row.reportingTime
          ? row.reportingTime.toDate().toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
          : ''}
      </span>
    ),
  },
  {
    field: 'currency',
    headerName: `Cost (O/${unit})`,
    renderCell: ({ row }: GridCellParams) => (
      <div style={{ color: row.dontSum ? '#ef4444' : 'inherit' }}>
        {row.ratio && row.ratio > 0
          ? row.ratio
          : row.actualPrice && row.currency
          ? formatToPriceInDollars(row.actualPrice, 1, row.currency)
          : '-'}
      </div>
    ),
    type: 'string',
  },
  {
    field: 'actualPrice',
    headerName: `Cost ($/${unit})`,
    renderCell: ({ row }: GridCellParams) => (
      <div style={{ color: row.dontSum ? '#ef4444' : 'inherit' }}>
        {row.ratio && row.ratio > 0
          ? row.ratio
          : row.actualPrice
          ? formatToPriceInDollars(row.actualPrice * getCurrencyInUSD(row.currency), 1)
          : '-'}
      </div>
    ),
    type: 'string',
  },
  {
    field: 'priceMt',
    headerName: `Value (${unit})`,
    renderCell: ({ row }: GridCellParams) => (
      <div style={{ color: row.dontSum ? '#ef4444' : 'inherit' }}>
        {row.ratio && row.ratio > 0 ? row.ratio : formatToPriceInDollars(price, 1)}
      </div>
    ),
    type: 'string',
  },
  // {
  //   field: 'totalOrigPrice',
  //   headerName: 'Total original cost',
  //   renderCell: ({ row }: GridCellParams) => (
  //     <div>{row.actualPrice ? formatToPriceInDollars(row.quantity * row.actualPrice, 1, row.currency) : '-'}</div>
  //   ),
  //   type: 'string',
  // },
  {
    field: 'totalPriceMt',
    headerName: 'Total cost $',
    renderCell: ({ row }: GridCellParams) => (
      <div style={{ color: row.dontSum ? '#ef4444' : 'inherit' }}>
        {row.ratio && row.ratio > 0
          ? row.ratio
          : row.actualPrice
          ? formatToPriceInDollars(row.quantity * (row.actualPrice * getCurrencyInUSD(row.currency)), 1)
          : '-'}
      </div>
    ),
    type: 'string',
  },
  {
    field: 'totalValPrice',
    headerName: 'Total value',
    renderCell: ({ row }: GridCellParams) => (
      <div style={{ color: row.dontSum ? '#ef4444' : 'inherit' }}>
        {row.ratio && row.ratio > 0 ? row.ratio : formatToPriceInDollars(row.quantity * (price || 1), 1)}
      </div>
    ),
    type: 'string',
  },
  {
    field: 'dontSum',
    headerName: 'Exclude',
    width: 80,
    renderCell: ({ row }: GridCellParams) => (
      <div className="flex justify-center text-red-300">{row.dontSum ? 'Excluded' : ''}</div>
    ),
    type: 'boolean',
  },
];

export const getCommitmentsCols = (
  price: number,
  unit: string,
  _isGrouped: boolean = false,
  onGroupToggle?: (_groupKey: string) => void,
  expandedGroups?: Set<string>,
  materialsMap?: Record<string, Material>,
): GridColDef<SalesCommItem>[] => [
  {
    field: 'clientName',
    headerName: `Client Name`,
    renderCell: ({ row }: GridCellParams) => {
      const groupedRow = row as GroupedSalesCommItem;
      if (groupedRow.isGroup) {
        const isExpanded = expandedGroups?.has(groupedRow.groupKey || '');
        return (
          <div
            className="flex items-center gap-1 font-bold cursor-pointer hover:bg-gray-100 w-full h-full px-2"
            onClick={() => onGroupToggle?.(groupedRow.groupKey || '')}
          >
            {isExpanded ? <KeyboardArrowDown /> : <KeyboardArrowRight />}
            <span>{groupedRow.clientName}</span>
          </div>
        );
      }
      return (
        <div className="pl-10" style={{ color: row.dontSum ? '#ef4444' : 'inherit' }}>
          {row.clientName}
        </div>
      );
    },
    type: 'string',
  },
  {
    field: 'reportingTime',
    headerName: 'Due date',
    type: 'date',
    renderCell: ({ row }: GridCellParams) => {
      return (row as GroupedSalesCommItem).isGroup ? (
        <div></div>
      ) : (
        <div className="pl-2" style={{ color: row.dontSum ? '#ef4444' : 'inherit' }}>
          {row.date?.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) || ''}
        </div>
      );
    },
  },
  {
    field: 'quantity',
    headerName: 'Quantity',
    renderCell: ({ row }: GridCellParams) => {
      const groupedRow = row as GroupedSalesCommItem;
      const displayValue = groupedRow.isGroup ? groupedRow.totalQuantity : row.value;
      const className = groupedRow.isGroup ? 'font-bold' : 'pl-2';
      return (
        <div className={className} style={{ color: row.dontSum ? '#ef4444' : 'inherit' }}>
          {formatToShortString(displayValue, 1)} {unit}
        </div>
      );
    },
    type: 'string',
  },
  {
    field: 'actualPrice',
    headerName: `Actual price`,
    renderCell: ({ row }: GridCellParams) => {
      const groupedRow = row as GroupedSalesCommItem;
      const actualPrice = groupedRow.isGroup
        ? (groupedRow.totalActualPrice || 1) / (groupedRow.totalQuantity || 1)
        : row.actualPrice;
      const className = groupedRow.isGroup ? 'font-bold' : 'pl-2';
      const currency = materialsMap?.[row.materialId]?.currency || 'USD';

      return (
        <div className={className} style={{ color: row.dontSum ? '#ef4444' : 'inherit' }}>
          {formatToPriceInDollars(actualPrice, 1, currency)}
        </div>
      );
    },
    type: 'string',
  },
  {
    field: 'totalActualPrice',
    headerName: `Total actual price`,
    renderCell: ({ row }: GridCellParams) => {
      const groupedRow = row as GroupedSalesCommItem;
      const totalPrice = groupedRow.isGroup ? groupedRow.totalActualPrice : row.actualPrice * row.value;
      const className = groupedRow.isGroup ? 'font-bold' : 'pl-2';
      const currency = materialsMap?.[row.materialId]?.currency || 'USD';
      return (
        <div className={className} style={{ color: row.dontSum ? '#ef4444' : 'inherit' }}>
          {formatToPriceInDollars(totalPrice || 0, 1, currency)}
        </div>
      );
    },
    type: 'string',
  },
  {
    field: 'priceMt',
    headerName: `Value (${unit})`,
    renderCell: ({ row }: GridCellParams) => {
      const groupedRow = row as GroupedSalesCommItem;
      const className = groupedRow.isGroup ? 'font-bold' : 'pl-2';
      return (
        <div className={className} style={{ color: row.dontSum ? '#ef4444' : 'inherit' }}>
          {formatToPriceInDollars(price, 1)}
        </div>
      );
    },
    type: 'string',
  },
  {
    field: 'totalValPrice',
    headerName: 'Total value',
    renderCell: ({ row }: GridCellParams) => {
      const groupedRow = row as GroupedSalesCommItem;
      const displayValue = groupedRow.isGroup ? groupedRow.totalQuantity : row.value;
      const totalValue = displayValue * (price || 1);
      const className = groupedRow.isGroup ? 'font-bold' : 'pl-2';
      return (
        <div className={className} style={{ color: row.dontSum ? '#ef4444' : 'inherit' }}>
          {formatToPriceInDollars(totalValue, 1)}
        </div>
      );
    },
    type: 'string',
  },
  {
    field: 'dontSum',
    headerName: 'Exclude',
    width: 80,
    renderCell: ({ row }: GridCellParams) => {
      const groupedRow = row as GroupedSalesCommItem;
      if (groupedRow.isGroup) return <div></div>;
      return <div className="flex justify-center text-red-300">{row.dontSum ? 'Excluded' : ''}</div>;
    },
    type: 'boolean',
  },
];
